import { createAsyncThunk } from '@reduxjs/toolkit'

import { callErrorMsg } from 'helpers/errorMsg'
import { RootStateType } from 'store'
import { getVendorDetails } from 'features/Enterprise/Vendor/services/addEditVendor'
import {
  postVendorReviewAPI,
  getVendorDetailRequest,
} from 'features/Enterprise/Vendor/services/vendorReview'
import {
  type VendorDetailsInterface,
  type PostVendorReviewDataType,
  type SubmissionReasonType,
} from 'features/Enterprise/Vendor/types/VendorManagement'
import { normalizeVendorDetailsFromApi } from 'features/Enterprise/Vendor/utils/ruleGroupsMapper'

import { setVendorDetails } from '../AddEditVendor/addEditVendorSlice'
import { fetchVendorRequests } from '../VendorList/vendorListAction'
import {
  SLICE_NAME,
  setIsReviewDrawerClose,
  setLoading,
  setVendorReviewData,
  setSubmissionReviewInfo,
  setVendorSubmissionPayload,
} from './vendorReviewSlice'

export const fetchVendorSubmissionData = createAsyncThunk(
  `${SLICE_NAME}/fetchVendorSubmissionData`,
  async (submissionId: number, { dispatch, getState }) => {
    try {
      const {
        auth: { userData },
      } = getState() as RootStateType
      const { data } = await getVendorDetailRequest(submissionId.toString())
      const normalizedRequestData = normalizeVendorDetailsFromApi(
        data.data?.request_data as unknown as VendorDetailsInterface,
      )
      dispatch(setVendorDetails(normalizedRequestData))
      dispatch(
        setVendorSubmissionPayload({
          data: normalizedRequestData,
          user: userData?.email,
          submission_id: submissionId,
        }),
      )
      dispatch(
        setSubmissionReviewInfo({
          reason: data?.data?.reason,
          reasons: (data.data?.reasons ?? []) as unknown as SubmissionReasonType[],
          status: data.data?.status || '',
          approvalPic: data.data?.approval_pic || '',
          rejectedPic: data.data?.reject_pic || '',
          revisionCount: data.data?.revision || 0,
          id: data.data?.id || 0,
        }),
      )
    } catch (error) {
      callErrorMsg(error)
    }
  },
)

export const fetchVendorReviewData = createAsyncThunk(
  `${SLICE_NAME}/fetchVendorReviewData`,
  async (id: number, { dispatch }) => {
    try {
      dispatch(setLoading(true))
      const vendorRequestDetail = await getVendorDetailRequest(id.toString())

      const vendorId = vendorRequestDetail.data?.data?.request_data?.vendor_id || 0
      let vendorDetails = {} as VendorDetailsInterface

      if (vendorId) {
        try {
          const resp = await getVendorDetails(vendorId.toString())
          vendorDetails = normalizeVendorDetailsFromApi(
            resp.data?.data as unknown as VendorDetailsInterface,
          )
        } catch (vendorDetailsError) {
          callErrorMsg(vendorDetailsError, 'Gagal memuat data vendor sebelum perubahan')
        }
      }

      const normalizedReviewDetails = normalizeVendorDetailsFromApi(
        vendorRequestDetail.data?.data?.request_data as unknown as VendorDetailsInterface,
      )

      dispatch(
        setVendorReviewData({
          submissionId: vendorRequestDetail?.data?.data?.id || null,
          reviewDetails: normalizedReviewDetails,
          vendorDetails,
        }),
      )
    } catch (error) {
      callErrorMsg(error)
    } finally {
      dispatch(setLoading(false))
    }
  },
)

export const submitVendorReview = createAsyncThunk(
  `${SLICE_NAME}/submitVendorReview`,
  async (
    {
      submissionId,
      data,
      cb,
    }: {
      submissionId?: number
      data: PostVendorReviewDataType
      cb?: () => void
    },
    { dispatch, getState },
  ) => {
    try {
      dispatch(setLoading(true))
      const {
        vendorReview: { vendorReviewId },
      } = getState() as RootStateType
      await postVendorReviewAPI(submissionId || vendorReviewId, data)

      if (!submissionId) {
        dispatch(setIsReviewDrawerClose())
      }
      dispatch(fetchVendorRequests(''))
      if (cb) cb()
    } catch (error) {
      callErrorMsg(error)
    } finally {
      dispatch(setLoading(false))
    }
  },
)
